import { Component, OnInit, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { ModalInvoiceAddDetailComponent } from '../../../modals/finance/invoice/modal-invoice-add-detail/modal-invoice-add-detail.component';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_FORMATS, MAT_DATE_LOCALE, DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { CityParkingAddressList, CityParkingCustomerInfoResponse } from '../../../../../core/models/customer.model';
import { Blank, FeeType } from '../../../../../core/models/information.model';
import { CityParkingAddressSelectResponse } from '../../../../../core/models/register-parking';
import { InvoiceDetailPayload, InvoiceFeeList, InvoiceHeaderResponse, InvoicePayLoad, UpdateInvoiceDetailsPayload } from '../../../../../core/models/invoice.model';
import { ApiFormService } from '../../../../../core/services/api-form.service';
import { ApiService } from '../../../../../core/services/api.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { VatSettingService } from '../../../../../core/services/vat-setting.service';
import { addVatToExclusive } from '../../../../../core/utils/vat-amount.util';
import { ModalAdminActivityHistoryComponent } from '../../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

// Custom Date Format สำหรับแสดงผล วว/ดด/ปป (DD/MM/YYYY)
export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearLabel: { year: 'numeric', month: 'short' } as Intl.DateTimeFormatOptions,
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearA11yLabel: { year: 'numeric', month: 'long' } as Intl.DateTimeFormatOptions,
  }
};

@Component({
  selector: 'app-invoice-detail',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    ModalInvoiceAddDetailComponent,
    PssLoadingComponent,
    PssConfirmModalComponent,
    MatDatepickerModule,
    MatNativeDateModule,
    ModalAdminActivityHistoryComponent,
  ],
  providers: [
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }
  ],
  templateUrl: './invoice-detail.component.html',
  styleUrl: './invoice-detail.component.css'
})
export class InvoiceDetailComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly apiFormService = inject(ApiFormService);
  private readonly apiService = inject(ApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  readonly vatSetting = inject(VatSettingService);

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  customers: CityParkingCustomerInfoResponse[] = [];
  addresses: CityParkingAddressSelectResponse[] = [];
  feeTypes: FeeType[] = [];
  banks: Blank[] = [];
  invoiceFeeList: InvoiceFeeList[] = [];

  name = '';
  selectedCustomerId: number | null = null;
  selectedAddressId: number | null = null;
  selectedFeeTypeId: number | null = null;
  selectedBankId: number | null = null;
  dueDate: Date | string = this.getDefaultDueDate();
  remark = '';
  userId = 0;

  openDropdown: string | null = null;
  customerSearchText = '';
  items: InvoiceDetailPayload[] = [];

  subTotal = 0;
  vatAmount = 0;
  total = 0;

  isAddItemModalOpen = false;
  editingItemIndex: number | null = null;
  isLoading = false;
  isConfirmSaveModalOpen = false;
  readonly editInvoiceId = Number(this.route.snapshot.paramMap.get('id') ?? 0);

  constructor() {
    const userIdLocal = localStorage.getItem('userId');
    this.userId = userIdLocal
      ? Number(userIdLocal)
      : Number(this.cookieService.get('user_id'));

    // Reactively load banks when the selected site changes
    effect(() => {
      const site = this.siteState.selectedSite();
      untracked(() => {
        this.selectedBankId = null;
        this.banks = [];
        if (site?.id) {
          this.loadBanksForSite(site.id);
        }
        this.loadCustomersForSite(site?.id ?? null);
      });
    });
  }

  /**
   * Standard Angular lifecycle hook initialized components state.
   */
  ngOnInit(): void {
    if (!this.userId) {
      const userIdLocal = localStorage.getItem('userId');
      this.userId = userIdLocal
        ? Number(userIdLocal)
        : Number(this.cookieService.get('user_id'));
    }

    this.loadMasterData();
  }

  // ==========================================
  // ============ Normal Functions ============
  // ==========================================

  /**
   * Gets the active selected Site ID.
   */
  get selectedSiteId(): number | null {
    return this.siteState.selectedSite()?.id ?? null;
  }

  /**
   * Gets the active selected Site Name.
   */
  get selectedSiteName(): string {
    return this.siteState.selectedSite()?.name ?? '';
  }

  get isEditMode(): boolean {
    return this.editInvoiceId > 0;
  }

  get isCustomerLocked(): boolean {
    return this.isEditMode;
  }

  get isHeaderLocked(): boolean {
    return !this.isEditMode && this.items.length > 0;
  }

  private applyCustomerDisplay(): void {
    if (!this.selectedCustomerId) {
      return;
    }
    const current = this.customers?.find((c) => c?.id === this.selectedCustomerId);
    if (current) {
      this.customerSearchText = `${current.name} (${current.phone})`;
    }
  }

  private getDefaultDueDate(): Date {
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 15);
    return dueDate;
  }

  /**
   * Filters the customers array reactively based on typed text.
   */
  get filteredCustomers(): CityParkingCustomerInfoResponse[] {
    if (!this.customerSearchText) {
      return this.customers;
    }
    const search = this.customerSearchText.toLowerCase();
    return this.customers.filter(c =>
      (c.name?.toLowerCase().includes(search) || false) ||
      (c.phone?.toLowerCase().includes(search) || false)
    );
  }

  /**
   * Prepares customer list formatted options.
   */
  get customerOptions() {
    return this.filteredCustomers.map(c => ({ label: `${c.name} (${c.phone})`, value: c.id }));
  }

  /**
   * Prepares address list formatted options.
   */
  get addressOptions() {
    return this.addresses.map(a => ({ label: a.name, value: a.id }));
  }

  /**
   * Prepares payment types formatted options.
   */
  get paymentTypeOptions() {
    return this.feeTypes.map(f => ({ label: f.name, value: f.id }));
  }

  /**
   * Prepares bank accounts formatted options.
   */
  get bankOptions() {
    return this.banksList.map(b => ({ label: `${b.bankName} ${b.accountNumber}`, value: b.id }));
  }

  /**
   * Resolves the active bank record object.
   */
  get selectedBank(): Blank | undefined {
    return this.banksList.find(b => b.id === this.selectedBankId);
  }

  private get banksList(): Blank[] {
    return Array.isArray(this.banks) ? this.banks : [];
  }

  /**
   * Simple UI validator checking header properties completeness.
   */
  get isHeaderValid(): boolean {
    if (this.isEditMode) {
      return true;
    }
    if (!this.name || !this.selectedCustomerId || !this.selectedSiteId || !this.selectedFeeTypeId) {
      return false;
    }
    if (this.selectedFeeTypeId === 3 && !this.selectedBankId) {
      return false;
    }
    return true;
  }

  /**
   * Toggles standard custom select dropdown states.
   */
  toggleDropdown(key: string, event: Event): void {
    if (this.isHeaderLocked) return;
    event.stopPropagation();
    if (this.openDropdown === key) {
      this.openDropdown = null;
    } else {
      this.openDropdown = key;
    }
  }

  /**
   * Closes active custom dropdowns and handles text validation revert standard for autocomplete.
   */
  closeDropdowns(): void {
    if (this.openDropdown === 'customers') {
      const matched = this.customers?.find(c => `${c?.name} (${c?.phone})` === this.customerSearchText);
      if (matched) {
        if (this.selectedCustomerId !== matched.id) {
          this.selectedCustomerId = matched.id;
          this.onCustomerChange();
        }
      } else {
        const current = this.customers?.find(c => c?.id === this.selectedCustomerId);
        this.customerSearchText = current ? `${current.name} (${current.phone})` : '';
      }
    }
    this.openDropdown = null;
  }

  /**
   * Resolves UI text strings display for different dropdown items.
   */
  getOptionName(key: string, value: unknown): string {
    if (value === null || value === undefined) return '';
    switch (key) {
      case 'customers': {
        const cust = this.customers.find(c => c.id === value);
        return cust ? `${cust.name} (${cust.phone})` : '';
      }
      case 'addresses':
        return this.addresses.find(a => a.id === value)?.name || '';
      case 'paymentTypes':
        return this.feeTypes.find(f => f.id === value)?.name || '';
      case 'banks': {
        const bank = this.banksList.find(b => b.id === value);
        return bank ? `${bank.bankName} ${bank.accountNumber}` : '';
      }
      default:
        return '';
    }
  }

  /**
   * Handles selection mapping update.
   */
  selectOption(key: string, value: unknown): void {
    switch (key) {
      case 'customers':
        this.selectedCustomerId = value as number;
        this.onCustomerChange();
        const current = this.customers?.find(c => c?.id === this.selectedCustomerId);
        this.customerSearchText = current ? `${current.name} (${current.phone})` : '';
        break;
      case 'addresses':
        this.selectedAddressId = value as number;
        break;
      case 'paymentTypes':
        this.selectedFeeTypeId = value as number;
        break;
      case 'banks':
        this.selectedBankId = value as number;
        break;
    }
    this.openDropdown = null;
  }

  /**
   * Opens the add detail modal.
   */
  openAddItemModal(): void {
    if (!this.selectedSiteId && !this.isEditMode) {
      this.toast.error('กรุณาเลือก Site จาก Sidebar ก่อน');
      return;
    }
    this.editingItemIndex = null;
    this.isAddItemModalOpen = true;
  }

  openEditItemModal(index: number): void {
    this.editingItemIndex = index;
    this.isAddItemModalOpen = true;
  }

  /**
   * Closes the add detail modal.
   */
  closeAddItemModal(): void {
    this.isAddItemModalOpen = false;
    this.editingItemIndex = null;
  }

  /**
   * Handles new item appended from modal.
   */
  handleModalSave(newItem: InvoiceDetailPayload): void {
    if (this.editingItemIndex === null) {
      this.items.push(newItem);
    } else {
      this.items[this.editingItemIndex] = newItem;
    }
    this.calculateTotals();
    this.closeAddItemModal();
  }

  /**
   * Removes selected item from the array list.
   */
  removeItem(index: number): void {
    this.items.splice(index, 1);
    this.calculateTotals();
  }

  /**
   * Calculates subtotal, vat and totals.
   */
  calculateTotals(): void {
    this.subTotal = 0;
    this.vatAmount = 0;
    this.total = 0;

    this.items.forEach(item => {
      this.subTotal += this.getItemBaseTotal(item);
      this.vatAmount += this.getItemVatTotal(item);
    });

    this.total = this.subTotal + this.vatAmount;
  }

  /**
   * Retrieves base total amount for an item line.
   */
  getItemBaseTotal(item: InvoiceDetailPayload): number {
    return item.amount * item.quantity;
  }

  /**
   * VAT ของทั้งบรรทัด = (unit amount × อัตรา VAT) × qty
   * หมายเหตุ: API เก็บ city_parking_invoice_details.vat เป็น VAT ต่อหน่วย
   * ห้ามใช้ item.vat ตรงๆ เป็นยอดบรรทัด (ผิดเมื่อ qty > 1)
   */
  getItemVatTotal(item: InvoiceDetailPayload): number {
    const feeInfo = this.findInvoiceFee(item);
    if (!feeInfo?.vat) {
      return 0;
    }
    const qty = item.quantity > 0 ? item.quantity : 1;
    const unitVat = addVatToExclusive(item.amount, this.vatSetting.ratePercent()).vatAmount;
    return Math.round(unitVat * qty * 100) / 100;
  }

  getItemGrandTotal(item: InvoiceDetailPayload): number {
    return this.getItemBaseTotal(item) + this.getItemVatTotal(item);
  }

  private findInvoiceFee(item: InvoiceDetailPayload): InvoiceFeeList | undefined {
    return this.invoiceFeeList.find((fee) => fee.id === item.invoiceFeeId)
      ?? this.invoiceFeeList.find((fee) => fee.feeId === item.feeId);
  }

  /**
   * Navigates back to parent invoices list path.
   */
  backToList(): void {
    this.router.navigate(['/finance/invoice-list']);
  }

  /**
   * Validates form parameters prior to submitting.
   */
  validateForm(): boolean {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.toast.error('กรุณาเลือก Site จาก Sidebar ก่อน');
      return false;
    }
    if (!this.name) {
      this.toast.error('กรุณาระบุชื่อผู้รับ');
      return false;
    }
    if (!this.isEditMode && !this.selectedCustomerId) {
      this.toast.error('กรุณาเลือกลูกค้า');
      return false;
    }
    if (!this.selectedFeeTypeId) {
      this.toast.error('กรุณาเลือกประเภทการชำระเงิน');
      return false;
    }
    if (this.selectedFeeTypeId === 3 && !this.selectedBankId) {
      this.toast.error('กรุณาเลือกธนาคาร');
      return false;
    }
    if (this.items.length === 0) {
      this.toast.error('กรุณาเพิ่มรายการสินค้า/บริการ');
      return false;
    }
    return true;
  }

  /**
   * Triggers validations. Shows confirmation dialog if successful.
   */
  submit(): void {
    if (!this.validateForm()) {
      return;
    }
    this.isConfirmSaveModalOpen = true;
  }

  // ==========================================
  // ============= API Functions =============
  // ==========================================

  /**
   * Loads initial customer registry, payment configurations, and fee details.
   */
  loadMasterData(): void {
    this.isLoading = true;

    this.apiFormService.getFeeType().subscribe({
      next: (res) => {
        this.feeTypes = res;
      }
    });

    this.apiService.getInvoiceFeeList().subscribe({
      next: (res) => {
        this.invoiceFeeList = res;
        if (this.isEditMode) {
          this.loadInvoiceForEdit();
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Get Invoice Fee List Error:', err);
        this.isLoading = false;
      }
    });
  }

  private loadInvoiceForEdit(): void {
    this.isLoading = true;
    this.apiService.getCityParkingInvoiceById(this.editInvoiceId).subscribe({
      next: (invoice) => {
        console.log('Load Invoice For Edit:', invoice);
        this.name = invoice.name ?? '';
        this.remark = invoice.remark ?? '';
        this.dueDate = invoice.dueDate ? new Date(invoice.dueDate) : this.getDefaultDueDate();
        this.selectedFeeTypeId = this.resolveFeeTypeId(invoice);

        const raw = invoice as unknown as Record<string, unknown>;
        const fallbackAddress = raw['CityParkingAddress'] as CityParkingAddressList | undefined;
        const address = invoice.cityParkingAddress ?? fallbackAddress;
        const customerId = Number(
          invoice.cpCustomerId
          ?? raw['CpCustomerId']
          ?? address?.cpCustomerId
          ?? 0
        );
        this.selectedCustomerId = customerId > 0 ? customerId : null;
        this.customerSearchText = String(invoice.customerName ?? raw['CustomerName'] ?? this.customerSearchText);
        this.applyCustomerDisplay();

        const addressId = Number(address?.id ?? 0);
        if (this.selectedCustomerId) {
          this.loadAddressesForCustomer(this.selectedCustomerId, addressId > 0 ? addressId : null);
        }

        this.items = (invoice.invoiceDetailResponses ?? []).map((item) => {
          const fee = this.invoiceFeeList.find((option) => option.name === item.invoiceFeeName);
          return {
            id: item.id,
            description: item.description || item.name || '',
            feeId: fee?.feeId ?? 0,
            invoiceFeeId: fee?.id ?? 0,
            quantity: item.quantity || 1,
            amount: item.fee || 0,
            vat: item.vat ?? 0,
          };
        });
        this.calculateTotals();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Get Invoice Header By Id Error:', err);
        this.isLoading = false;
        this.toast.error('ไม่สามารถโหลดรายละเอียดใบแจ้งหนี้ได้');
      },
    });
  }

  private resolveFeeTypeId(invoice: InvoiceHeaderResponse): number | null {
    const raw = invoice as unknown as Record<string, unknown>;
    const feeTypeId = Number(
      raw['feeTypeId'] ?? raw['FeeTypeId'] ?? raw['fee_type_id'] ?? raw['fee_typeId'] ?? 0
    );
    if (feeTypeId) {
      return feeTypeId;
    }

    const feeTypeName = String(raw['feeTypeName'] ?? raw['FeeTypeName'] ?? raw['fee_type_name'] ?? '');
    return this.feeTypes.find((feeType) => feeType.name === feeTypeName)?.id ?? null;
  }

  private buildUpdateInvoiceDetailsPayload(): UpdateInvoiceDetailsPayload {
    return {
      id: this.editInvoiceId || 0,
      feeTypeId: this.selectedFeeTypeId ?? 0,
      userId: this.userId || 0,
      name: this.name,
      cpAddressId: this.selectedAddressId || 0,
      dueDate: this.formatDueDate(this.dueDate),
      remark: this.remark,
      invoiceDetailPayload: this.items.map((item) => ({
        id: item.id ?? 0,
        description: item.description ?? '',
        feeId: item.feeId ?? 0,
        invoiceFeeId: item.invoiceFeeId ?? 0,
        quantity: item.quantity ?? 0,
        amount: item.amount ?? 0,
      })),
    };
  }

  private formatDueDate(value: Date | string): string {
    if (!value) {
      return '';
    }
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) {
      return String(value);
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /**
   * Loads Bank list options corresponding to active Site.
   */
  loadBanksForSite(siteId: number): void {
    this.apiFormService.getBank(siteId).subscribe({
      next: (res) => {
        console.log("Load Banks For Site: ", res)
        this.banks = this.normalizeBanks(res);
        if (this.banks.length > 0) {
          this.selectedBankId = this.banks[0].id;
        }
      },
      error: (err) => {
        console.error('[API Error] getBank:', err);
        this.banks = [];
        this.selectedBankId = null;
        this.toast.error('ไม่สามารถโหลดรายการธนาคารได้');
      }
    });
  }

  /** API may return array or wrapped object — always normalize to Blank[] */
  private normalizeBanks(res: unknown): Blank[] {
    if (!res) return [];

    let list: unknown = res;
    if (!Array.isArray(list) && typeof list === 'object') {
      const obj = list as Record<string, unknown>;
      const nested = obj['data'] ?? obj['items'] ?? obj['bankAccounts'] ?? obj['result'];
      if (Array.isArray(nested)) {
        list = nested;
      } else if ('id' in obj || 'Id' in obj) {
        list = [obj];
      } else {
        console.warn('[getBank] unexpected response shape:', res);
        return [];
      }
    }

    if (!Array.isArray(list)) return [];

    return list.map((item) => this.mapBankItem(item));
  }

  private mapBankItem(raw: unknown): Blank {
    const r = (raw ?? {}) as Record<string, unknown>;
    return {
      id: Number(r['id'] ?? r['Id'] ?? 0),
      name: String(r['name'] ?? r['Name'] ?? ''),
      bankName: String(r['bankName'] ?? r['BankName'] ?? ''),
      accountName: String(r['accountName'] ?? r['AccountName'] ?? ''),
      accountNumber: String(r['accountNumber'] ?? r['AccountNumber'] ?? ''),
    };
  }

  /**
   * Triggers on customer selection changes to load sub addresses list.
   */
  onCustomerChange(): void {
    this.loadAddressesForCustomer(this.selectedCustomerId, null);
  }

  private loadAddressesForCustomer(customerId: number | null, preferredAddressId: number | null): void {
    this.selectedAddressId = null;
    this.addresses = [];
    if (!customerId) {
      return;
    }
    this.apiFormService.getCityAddressForSelectByCustomerId(customerId).subscribe({
      next: (res) => {
        this.addresses = res;
        if (preferredAddressId && this.addresses.some((a) => a.id === preferredAddressId)) {
          this.selectedAddressId = preferredAddressId;
          return;
        }
        if (this.addresses.length > 0) {
          this.selectedAddressId = this.addresses[0].id;
        }
      }
    });
  }

  /**
   * Submits invoice creation request payloads to the backend API after confirmation.
   */
  confirmSubmit(): void {
    this.isConfirmSaveModalOpen = false;
    if (this.isEditMode) {
      this.updateInvoiceDetails();
      return;
    }
    const siteId = this.selectedSiteId;
    if (!siteId) return;

    const payload: InvoicePayLoad = {
      id: 0,
      name: this.name,
      invoiceNo: '',
      siteId,
      cpCustomerId: this.selectedCustomerId!,
      cpAddressId: this.selectedAddressId || 0,
      feeTypeId: this.selectedFeeTypeId!,
      bankAccountId: this.selectedBankId || 0,
      userId: this.userId,
      dueDate: this.formatDueDate(this.dueDate),
      remark: this.remark,
      invoiceDetailPayload: this.items,
    };

    this.isLoading = true;
    console.log('Sending Invoice Payload:', payload);

    this.apiService.createCityParkingInvoice(payload).subscribe({
      next: (res) => {
        this.isLoading = false;
        console.log('Create Invoice Response:', res);
        if (res.isSuccess) {
          this.toast.success('สร้างใบแจ้งหนี้สำเร็จ');
          this.backToList();
        } else {
          this.toast.error(res.message || 'สร้างใบแจ้งหนี้ไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Create Invoice Error:', err);
        this.toast.error('เกิดข้อผิดพลาดในการบันทึก');
      }
    });
  }

  private updateInvoiceDetails(): void {
    const payload = this.buildUpdateInvoiceDetailsPayload();
    console.log('Update Invoice Details Payload JSON:\n' + JSON.stringify(payload, null, 2));

    this.isLoading = true;
    this.apiService.updateCityParkingInvoice(payload).subscribe({
      next: (res) => {
        this.isLoading = false;
        console.log('Update Invoice Details Response:', res);
        if (res.isSuccess) {
          this.toast.success('แก้ไขรายละเอียดใบแจ้งหนี้สำเร็จ');
          this.backToList();
        } else {
          this.toast.error(res.message || 'แก้ไขรายละเอียดใบแจ้งหนี้ไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Update Invoice Details Error:', err);
        this.toast.error('เกิดข้อผิดพลาดในการบันทึกการแก้ไข');
      },
    });
  }

  get canShowActivityHistory(): boolean {
    return this.isEditMode && this.editInvoiceId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }

  private customerRequestSeq = 0;

  /**
   * โหลดลูกค้า (city_parking_customer) เฉพาะลานที่เลือกบนแถบด้านข้าง
   */
  private loadCustomersForSite(siteId: number | null): void {
    const seq = ++this.customerRequestSeq;
    if (!siteId) {
      this.customers = [];
      this.resetCustomerIfNotInSite();
      return;
    }
    this.apiFormService.getCityParkingCustomerList(siteId).subscribe({
      next: (res) => {
        if (seq !== this.customerRequestSeq) return;
        this.customers = res ?? [];
        this.resetCustomerIfNotInSite();
        this.applyCustomerDisplay();
      },
      error: (err) => {
        if (seq !== this.customerRequestSeq) return;
        console.error('Get Customer List Error:', err);
        this.customers = [];
        this.toast.error('ไม่สามารถโหลดข้อมูลลูกค้าได้');
      },
    });
  }

  /** โหมดสร้าง: ลูกค้าที่เลือกไว้ไม่อยู่ในลานปัจจุบัน → ล้างการเลือก */
  private resetCustomerIfNotInSite(): void {
    if (this.isEditMode || !this.selectedCustomerId) return;
    if (this.customers.some((c) => c.id === this.selectedCustomerId)) return;
    this.selectedCustomerId = null;
    this.customerSearchText = '';
    this.loadAddressesForCustomer(null, null);
  }
}

